if (!customElements.get('collection-infinite-scroll')) {
  class CollectionInfiniteScroll extends HTMLElement {
    static ROOT_MARGIN = '0px 0px 0px 0px';

    static MIN_SCROLL_BETWEEN_LOADS = 300;

    static getLoadTrigger() {
      const grid = document.getElementById('product-grid');
      if (!grid) return null;

      const items = grid.querySelectorAll('.grid__item:not(.grid__item--banner):not(.grid__item--skeleton)');
      return items.length ? items[items.length - 1] : null;
    }

    static mount(element) {
      if (!element || element.isLoading) return;

      element._infiniteScrollObserver?.disconnect();
      element._infiniteScrollMounted = false;

      const statusEl = element.querySelector('.collection-infinite-scroll__status');
      const trigger = CollectionInfiniteScroll.getLoadTrigger();

      if (!trigger || !element.hasMore()) return;

      CollectionInfiniteScroll.ensureScrollListener(element);

      element._infiniteScrollMounted = true;
      element._infiniteScrollObserver = new IntersectionObserver(
        (entries) => {
          if (!entries[0]?.isIntersecting) return;
          CollectionInfiniteScroll.tryLoadIfReady(element);
        },
        { rootMargin: CollectionInfiniteScroll.ROOT_MARGIN, threshold: 0.1 }
      );

      element._infiniteScrollObserver.observe(trigger);
      element._statusEl = statusEl;
      element._observedTrigger = trigger;
    }

    static ensureScrollListener(element) {
      if (element._scrollListenerAttached) return;

      element._scrollListenerAttached = true;
      element._userHasScrolled = false;
      element._scrollYAtLastLoad = null;

      const onScroll = () => {
        element._userHasScrolled = true;
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      element._scrollCleanup = () => window.removeEventListener('scroll', onScroll);
    }

    static hasScrolledEnoughSinceLastLoad(element) {
      if (element._scrollYAtLastLoad === null) return true;
      return window.scrollY >= element._scrollYAtLastLoad + CollectionInfiniteScroll.MIN_SCROLL_BETWEEN_LOADS;
    }

    static tryLoadIfReady(element) {
      if (element.isLoading || !element.hasMore() || !element._userHasScrolled) return;
      if (!CollectionInfiniteScroll.hasScrolledEnoughSinceLastLoad(element)) return;

      const trigger = CollectionInfiniteScroll.getLoadTrigger();
      if (!trigger || trigger !== element._observedTrigger) return;

      CollectionInfiniteScroll.loadNextPage(element);
    }

    static mountAll(root = document) {
      root.querySelectorAll('collection-infinite-scroll').forEach((element) => {
        if (element._infiniteScrollMounted) return;
        CollectionInfiniteScroll.mount(element);
      });
    }

    static buildFetchUrl(element) {
      const nextPage = element.getNextPage();
      if (!nextPage) return null;

      const params = new URLSearchParams(window.location.search);
      params.set('page', String(nextPage));
      params.set('section_id', element.dataset.sectionId);

      return `${window.location.pathname}?${params.toString()}`;
    }

    static parseSectionHtml(html) {
      return new DOMParser().parseFromString(html, 'text/html');
    }

    static findProductGrid(doc, sectionId) {
      return (
        doc.querySelector(`#shopify-section-${sectionId} #product-grid`) ||
        doc.querySelector('#ProductGridContainer #product-grid') ||
        doc.getElementById('product-grid')
      );
    }

    static findInfiniteScrollMeta(doc, sectionId) {
      return (
        doc.querySelector(`#shopify-section-${sectionId} collection-infinite-scroll`) ||
        doc.querySelector('#ProductGridContainer collection-infinite-scroll') ||
        doc.querySelector('collection-infinite-scroll')
      );
    }

    static getSkeletonTemplate(element) {
      const sectionId = element.dataset.sectionId;
      return document.getElementById(`ProductGridSkeleton-${sectionId}`);
    }

    static showSkeletons(element) {
      const grid = document.getElementById('product-grid');
      const template = CollectionInfiniteScroll.getSkeletonTemplate(element);
      if (!grid || !template) return;

      const count = parseInt(element.dataset.skeletonCount, 10) || 8;
      const templateItems = template.content.querySelectorAll('.grid__item--skeleton');
      if (!templateItems.length) return;

      for (let i = 0; i < count; i += 1) {
        const source = templateItems[i % templateItems.length];
        grid.appendChild(source.cloneNode(true));
      }
    }

    static hideSkeletons() {
      document.querySelectorAll('#product-grid .grid__item--skeleton').forEach((item) => {
        item.remove();
      });
    }

    static async loadNextPage(element) {
      if (element.isLoading || !element.hasMore()) return;

      const fetchUrl = CollectionInfiniteScroll.buildFetchUrl(element);
      if (!fetchUrl) return;

      element.isLoading = true;
      element._scrollYAtLastLoad = window.scrollY;
      element._infiniteScrollObserver?.disconnect();
      element._infiniteScrollMounted = false;
      element._observedTrigger = null;
      element.setLoading(true);

      try {
        const response = await fetch(fetchUrl);
        if (!response.ok) throw new Error(`Failed to fetch page (${response.status})`);

        const parsed = CollectionInfiniteScroll.parseSectionHtml(await response.text());
        const sectionId = element.dataset.sectionId;
        const newGrid = CollectionInfiniteScroll.findProductGrid(parsed, sectionId);
        const grid = document.getElementById('product-grid');

        if (!newGrid || !grid) throw new Error('Product grid not found in response');

        const newItems = newGrid.querySelectorAll('.grid__item:not(.grid__item--banner)');
        if (!newItems.length) throw new Error('No products returned for next page');

        CollectionInfiniteScroll.hideSkeletons();

        const currentCount = grid.querySelectorAll('.grid__item:not(.grid__item--banner)').length;

        newItems.forEach((item, index) => {
          const clone = item.cloneNode(true);

          clone.querySelectorAll('img').forEach((img) => {
            img.loading = 'lazy';
          });

          if (clone.hasAttribute('data-cascade')) {
            clone.style.setProperty('--animation-order', currentCount + index + 1);
          }

          grid.appendChild(clone);
        });

        const newInfiniteScroll = CollectionInfiniteScroll.findInfiniteScrollMeta(parsed, sectionId);
        if (newInfiniteScroll) {
          element.dataset.currentPage = newInfiniteScroll.dataset.currentPage || '';
          element.dataset.totalPages = newInfiniteScroll.dataset.totalPages || '';
          element.dataset.nextPage = newInfiniteScroll.dataset.nextPage || '';
          if (newInfiniteScroll.dataset.nextUrl) {
            element.dataset.nextUrl = newInfiniteScroll.dataset.nextUrl;
          } else {
            delete element.dataset.nextUrl;
          }
        }

        if (element._statusEl) {
          element._statusEl.textContent = `${newItems.length} produtos carregados`;
        }

        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(document);
        }

        document.dispatchEvent(new CustomEvent('facets:grid-rendered'));
      } catch (error) {
        console.error('[collection-infinite-scroll]', error);
        CollectionInfiniteScroll.hideSkeletons();
      } finally {
        element.isLoading = false;
        element.setLoading(false);

        if (element.hasMore()) {
          requestAnimationFrame(() => {
            CollectionInfiniteScroll.mount(element);
          });
        }
      }
    }

    hasMore() {
      const currentPage = parseInt(this.dataset.currentPage, 10);
      const totalPages = parseInt(this.dataset.totalPages, 10);

      if (!Number.isNaN(currentPage) && !Number.isNaN(totalPages)) {
        return currentPage < totalPages;
      }

      return Boolean(this.dataset.nextPage || this.dataset.nextUrl);
    }

    getNextPage() {
      const nextPage = parseInt(this.dataset.nextPage, 10);
      if (!Number.isNaN(nextPage)) return nextPage;

      const currentPage = parseInt(this.dataset.currentPage, 10);
      if (!Number.isNaN(currentPage)) return currentPage + 1;

      if (this.dataset.nextUrl) {
        try {
          const nextUrl = new URL(this.dataset.nextUrl, window.location.href);
          const page = parseInt(nextUrl.searchParams.get('page'), 10);
          if (!Number.isNaN(page)) return page;
        } catch (error) {
          console.error('[collection-infinite-scroll]', error);
        }
      }

      return null;
    }

    setLoading(loading) {
      if (loading) {
        CollectionInfiniteScroll.showSkeletons(this);
        if (this._statusEl) {
          this._statusEl.textContent = 'Carregando mais produtos…';
        }
      } else {
        CollectionInfiniteScroll.hideSkeletons();
      }
    }

    connectedCallback() {
      requestAnimationFrame(() => {
        CollectionInfiniteScroll.mount(this);
      });
    }

    disconnectedCallback() {
      this._infiniteScrollObserver?.disconnect();
      this._infiniteScrollMounted = false;
      this._observedTrigger = null;
      this._scrollCleanup?.();
      this._scrollListenerAttached = false;
      CollectionInfiniteScroll.hideSkeletons();
    }
  }

  customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);

  document.addEventListener('DOMContentLoaded', () => {
    CollectionInfiniteScroll.mountAll();
  });

  document.addEventListener('facets:grid-rendered', () => {
    CollectionInfiniteScroll.mountAll();
  });
}
